import { afterNextRender, Component, inject, Injector, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { MenuService } from './menu.service';
import { ModeService } from "../../services/mode.service";
import { buildUrl } from "../../utils/urlUtils";
import { NgClass } from "@angular/common";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";

/**
 * Composant du menu de navigation de Pégase
 * utilisation:
 * ```
 *  <pegase-menu>
 *      <div id="nav-content"> // contenu du menu de navigation
 *        <mat-nav-list>
 *            <pegase-menu-entry></pegase-menu-entry>
 *            <pegase-menu-entry-with-children>
 *                <pegase-menu-entry></pegase-menu-entry>
 *                <pegase-menu-entry></pegase-menu-entry>
 *            </pegase-menu-entry-with-children>
 *        </mat-nav-list>
 *      </div>
 *
 *      <div id="main-content"> // contenu projeté de la page
 *        <pegase-header title="Contrôler le cursus" icon="school"> // le composant st fait pour fonctionner conjointement avec le header
 *           <lib-authentication-form></lib-authentication-form>
 *        </pegase-header>
 *
 *        // CONTENU DE LA PAGE
 *
 *      </div>
 *  </pegase-menu>
 * ```
 */
@Component({
  selector: 'lib-pegase-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  imports: [
    MatIcon,
    MatSidenavContainer,
    MatSidenav,
    NgClass,
    MatSidenavContent,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
  ],
  standalone: true
})
export class MenuComponent implements OnInit, OnDestroy {
  @ViewChild("snav", {static: true}) snav!: MatSidenav;
  public isNavOpened = true;
  fixedMenu = input<boolean>(false);
  /**
   * désactive le lien vers AGE
   */
  disableAgeLink = input<boolean>(false);
  private readonly modeService = inject(ModeService);
  private subscriptions: Subscription = new Subscription();
  private readonly menuService = inject(MenuService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  ngOnInit(): void {
    this.subscriptions.add(
      this.menuService.openMenu$.subscribe((value: boolean) => {
        if (value) {
          this.openNav();
        } else {
          this.closeNav();
        }
      })
    );

    this.subscribeToRouterEvents();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Cible du logo : AGE ou AAP selon le mode de l'application. Le mode n'est pas
   * un input — il vient de {@link ModeService}, comme pour le header et le pied
   * de page, afin que les trois ne puissent pas diverger.
   *
   * Méthode et non `computed` : `ModeService` n'expose pas de signaux, une
   * computed mettrait en cache la valeur lue au premier rendu et ne la
   * réévaluerait jamais.
   */
  public homeLink(): string | null {
    return this.disableAgeLink() ? null : buildUrl(this.modeService.configuration.ageApp);
  }

  public toggleNav(): void {
    this.menuService.toggleMenu();
  }

  /**
   * ne pas utiliser cette méthode à la main, utiliser la méthode toggleNav() pour passer par le service
   */
  private openNav(): void {
    this.isNavOpened = true;
  }

  /**
   * ne pas utiliser cette méthode à la main, utiliser la méthode toggleNav() pour passer par le service
   */
  private closeNav(): void {
    this.isNavOpened = false;
  }

  private subscribeToRouterEvents(): void {
    this.subscriptions.add(
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(event => event.urlAfterRedirects.split('?')[0]),
        distinctUntilChanged()
      ).subscribe(() => {
        afterNextRender(() => this.focusPageContent(), { injector: this.injector });
      })
    );
  }

  private focusPageContent(): void {
    const h1 = document.querySelector<HTMLElement>('main#top h1');
    const firstChild = document.querySelector<HTMLElement>('main#top > :first-child');
    const main = document.querySelector<HTMLElement>('main#top');
    const target = h1 ?? firstChild ?? main;
    if (target) {
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus();
    }
  }
}
